// eslint-disable-next-line you-dont-need-lodash-underscore/last
import { last, find } from 'lodash';

import { getIsSeason3 } from 'a/hub/selectors';
import { exitGame } from 'a/hub/actions/metaGameActions';
import { setRumGame, logRumError, logRumAction } from 'lib/DDRum';
import { getBookGameId } from 'lib/utilityFunctions';
import { updateEreaderStep } from 'a/hub/actions/questsActions';
import { notifyEreaderStepProgress } from 'root/activities/hub/actions/workshopActions';

import BOOK_STATUS from '../../data/book_status';
import {
  READING_ACTION_TYPES,
  READING_MODE_ACTION_TYPES,
  ACTIVITY_ACTION_TYPES,
  INTERACT_ACTION_TYPES,
  CAPTIONS_ACTION_TYPES,
  SET_ROUND_SESSION_ID,
} from './action_types';
import createAction from './action_creator';
import { updateRemainingPostReadingQuestions } from './post_reading';
import {
  reportCloseTableOfContents,
  reportExitChapter,
  reportExitReadingMode,
  reportNavigateTableOfContents,
  reportOpenTableOfContents,
  reportPageTurn,
  reportStartBook,
  reportStartChapter,
  reportStartReadingMode,
} from './reporting';
import { getChapterId, getBookId, getRoundSessionId, getSavedRoundSessionId } from '../selectors/reporting';
import READING_MODES from '../../data/reading_modes';
import ReaderAPI from '../../services/readerAPI';
import {
  setBookStatus,
  setTitleChapterId,
  setCurrentPage,
  setReadingMode,
  setBookAttempts,
  resetActivityAnswers,
  setShowAlerts,
  setAccurateResponseData,
  getLocalBookState,
  setRoundSessionId,
} from '../../services/api/state';
import getAvailableReadingModes from '../selectors/reading_modes';
import { getProgress, hasMetCompletionThreshold, getUnfinishedActivities } from '../selectors/progress';
import { getQuestDescriptor } from '../selectors/bookInfo';
import { validateChapterId } from '../../lib/utilityFunctions';

/**
 * save the roundSessionId for use between different sessions of the same chapter.
 * in order to connect sessions of the eReader, the roundSessionId needs to stay
 * consistent.
 */
export const saveRoundSessionId = () => (dispatch, getState) => {
  const state = getState();
  const savedRoundSessionId = getSavedRoundSessionId(state);
  const questDescriptor = getQuestDescriptor(state);
  const bookId = getBookId(state);

  if (!savedRoundSessionId) {
    const roundSessionId = getRoundSessionId(state);
    setRoundSessionId(bookId, questDescriptor, roundSessionId);
    dispatch(createAction(SET_ROUND_SESSION_ID, roundSessionId));
  }
};

export function updateCurrentPage(pageInfo, questDescriptor, { currentChapterId } = {}) {
  const { idref } = pageInfo;
  const { currentPageIndex } = pageInfo.pagination;
  const bookId = ReaderAPI.getBookId();

  validateChapterId({
    place: 'pagination',
    nextChapterId: idref,
    currentChapterId,
    isPractice: questDescriptor.isPractice,
    lockedChapterId: questDescriptor.chapterId,
    shouldSkipLog: idref === currentChapterId,
  });

  setCurrentPage(bookId, questDescriptor, idref, currentPageIndex);
}

export function setTotalQuestions(count) {
  return (dispatch, getState) => {
    const { eReader: { postReading: { questions } } } = getState();
    const totalQuestions = count + questions.length;

    dispatch({
      type: ACTIVITY_ACTION_TYPES.SET_TOTAL_QUESTIONS,
      payload: totalQuestions,
    });

    if (totalQuestions) {
      const { eReader: { bookInfo, accurateResponseData } } = getState();
      setAccurateResponseData(bookInfo.bookId, accurateResponseData);
    }
  };
}

export function navigateNext(dispatch) {
  dispatch({
    type: READING_ACTION_TYPES.LOCK_PAGINATION,
    payload: {
      isPaginationLocked: true,
    },
  });

  return ReaderAPI.goToNextPage();
}

export const changeReadingMode = () => (dispatch, getState) => {
  const state = getState();
  const {
    eReader: {
      readingMode: { currentMode },
    },
  } = state;
  const availableModes = getAvailableReadingModes(state.eReader);
  const currentModeIndex = availableModes.indexOf(currentMode);
  const mode = currentModeIndex === -1 ? availableModes[0] : availableModes[currentModeIndex + 1];

  if (!mode) {
    logRumError('Trying to set invalid reading mode.', { currentMode, availableModes });
    return null;
  }

  const bookId = ReaderAPI.getBookId();
  setReadingMode(bookId, mode);

  if (currentMode) {
    dispatch(reportExitReadingMode(currentMode));
  }
  dispatch(reportStartReadingMode(mode));

  logRumAction('changeReadingMode', { nextMode: mode, currentMode, availableModes });

  if (mode !== READING_MODES.CLEAN) {
    const page = mode === READING_MODES.CHALLENGE ? 0 : 9999;
    return ReaderAPI.goToPageIndex(page).then(() => {
      dispatch({
        type: READING_MODE_ACTION_TYPES.MODE_CHANGED,
        payload: { mode },
      });
    });
  }

  return dispatch({
    type: READING_MODE_ACTION_TYPES.MODE_CHANGED,
    payload: { mode },
  });
};

export function requestChangeMode(dispatch) {
  return dispatch({
    type: READING_MODE_ACTION_TYPES.CHANGE_REQUESTED,
  });
}

export const completeAllReadingModes = () => (dispatch, getState) => {
  const state = getState();
  const { eReader: { readingMode } } = state;

  dispatch(reportExitReadingMode(readingMode.currentMode));
  dispatch({
    type: READING_MODE_ACTION_TYPES.ALL_MODES_COMPLETED,
    payload: {
      hasMetCompletionThreshold: hasMetCompletionThreshold(state.eReader),
    },
  });
};

export function cancelReadingModeChangeRequest() {
  return (dispatch, getState) => {
    const { currentMode } = getState().eReader.readingMode;

    if (currentMode) {
      dispatch({ type: READING_MODE_ACTION_TYPES.CHANGE_REQUEST_CANCELED });
    }
  };
}

export function setBook(bookId, chapterId) {
  return {
    type: READING_ACTION_TYPES.SET_BOOK,
    payload: {
      bookId,
      chapterId,
    },
  };
}

export const openBook = bookOptions => (dispatch, getState) => {
  const { bookId, lockedChapterDocument } = bookOptions;
  const state = getState();
  const { eReader: { bookInfo } } = state;

  return ReaderAPI.init(bookOptions)
    .then(({ title, idref, pageInfo, columnsNumber, chapters }) => {
      const { titleChapterId } = bookInfo;
      if (!titleChapterId) {
        setTitleChapterId(bookId, idref);
      }

      setRumGame({ gameId: getBookGameId(bookId), level: `chapterId_${idref}` });

      dispatch({
        type: READING_ACTION_TYPES.OPEN_BOOK,
        payload: {
          pageInfo,
          title,
          columnsNumber,
          ready: true,
          titleChapterId: titleChapterId || idref,
          currentChapterId: idref,
          chapters,
        },
      });

      if (lockedChapterDocument || (chapters && chapters.length)) {
        const currentState = getState();
        const chapterId = getChapterId(currentState);

        dispatch(reportStartBook(chapterId));
        dispatch(reportStartChapter(chapterId));
      } else {
        dispatch(reportStartBook());
      }
    });
};

export const setReaderReady = ready => (dispatch) => {
  dispatch(saveRoundSessionId());

  dispatch({
    type: READING_ACTION_TYPES.SET_READER_READY,
    payload: {
      ready,
    },
  });
};

export const openChapter = chapter => (dispatch, getState) => {
  dispatch(setReaderReady(false));

  const currentState = getState();
  const { eReader: { bookInfo: { chapters } } } = currentState;
  const navigatedChapter = chapters.find(({ spineIdRef }) => spineIdRef === chapter.spineIdRef).index;
  dispatch(reportNavigateTableOfContents(navigatedChapter));

  return ReaderAPI.openChapter(chapter);
};

export const changeTocStatus = (closeSource, isOpened = false) => (dispatch) => {
  if (!isOpened) {
    dispatch(reportOpenTableOfContents());
    dispatch({
      type: READING_ACTION_TYPES.OPEN_TOC,
      payload: null,
    });
  } else {
    dispatch(reportCloseTableOfContents(closeSource));
    dispatch({
      type: READING_ACTION_TYPES.CLOSE_TOC,
      payload: null,
    });
  }
};

export const updatePaginationStatus = ({ canGoLeft = false, canGoRight = false } = {}) => ({
  type: READING_ACTION_TYPES.UPDATE_PAGINATION_STATUS,
  payload: {
    canGoLeft,
    canGoRight,
  },
});

export const updatePagination = (pageInfo) => (dispatch, getState) => {
  if (!pageInfo?.pagination) {
    return;
  }

  const state = getState();
  const {
    quest: { isPractice, lockedChapterDocument },
    bookInfo: { currentPage: currentPageIndex, currentChapterId },
  } = state.eReader;
  const {
    pagination: { currentPageIndex: nextPageIndex },
    idref: nextChapterId,
  } = pageInfo;
  const currentChapterIndex = getChapterId(state);

  dispatch({
    type: READING_ACTION_TYPES.UPDATE_PAGINATION,
    payload: {
      pageInfo,
    },
  });

  const updatedState = getState();
  const nextChapterIndex = getChapterId(updatedState);

  if (currentChapterIndex !== nextChapterIndex) {
    dispatch(reportExitChapter(currentChapterIndex));
    dispatch(reportStartChapter(nextChapterIndex));
  }

  if (currentPageIndex !== nextPageIndex || currentChapterId !== nextChapterId) {
    updateCurrentPage(pageInfo, { chapterId: lockedChapterDocument, isPractice }, { currentChapterId });
  }
};

export const goToPrevPage = () => (dispatch, getState) => {
  const currentState = getState();
  const { eReader: { bookInfo } } = currentState;

  dispatch({
    type: READING_ACTION_TYPES.LOCK_PAGINATION,
    payload: {
      isPaginationLocked: true,
    },
  });
  dispatch(reportPageTurn(false));

  const progress = getProgress(currentState.eReader);
  if (bookInfo.columnsNumber === 1) {
    const { currentChapterId, chapters } = bookInfo;
    const isOnTitlePage = progress === 100 && chapters[1] && chapters[1].spineIdRef === currentChapterId;
    if (progress === 0 || isOnTitlePage) {
      dispatch({
        type: READING_ACTION_TYPES.SET_READER_READY,
        payload: {
          ready: false,
        },
      });
    }
  }

  return ReaderAPI.goToPrevPage();
};

export function setConfirmationDialogVisibilty({ isVisible, shouldProceed = false }) {
  return (dispatch) => {
    dispatch({
      type: INTERACT_ACTION_TYPES.SET_CONFIRMATION_DIALOG_VISIBILITY,
      payload: {
        show: isVisible,
      },
    });

    if (shouldProceed) {
      dispatch(completeAllReadingModes());
    }
  };
}

export const goToNextPage = () => (dispatch, getState) => {
  const state = getState().eReader;

  dispatch(reportPageTurn(true));
  const { bookInfo, readingMode, interact, quest, postReading } = state;
  const { currentMode } = readingMode;
  const { displayConfirmation } = interact;

  const availableModes = getAvailableReadingModes(state);
  const { pages, chapters, currentChapterId } = bookInfo;
  const { isPractice } = quest;
  const progress = getProgress(state);

  const currentChapter = find(chapters, chapter => chapter.spineIdRef === currentChapterId);

  let isOnLockedChapter = false;
  // chapter books should always only show 1 chapter at a time. this line will make that so
  // assuming that normal chapters follow format 'page0XX' (as opposed to title and cover)
  if (currentChapter) {
    isOnLockedChapter = currentChapter.spineIdRef.includes('page');
  }

  const isLastPageOfLockedChapter = isOnLockedChapter && progress === 100;
  // chapter books are single column layout, is this redundant?
  const singleColumnLayout = bookInfo.columnsNumber === 1;

  // if we're in a chapter book...
  if (singleColumnLayout) {
    // what is different between this and isOnLockedChapter?
    const shouldHaveAnotherSegment =
        progress === 100 && pages.canGoRight && currentChapterId !== last(chapters).spineIdRef;

    const isOnCoverPage = progress === 0 && currentChapterId === chapters[0].spineIdRef;
    if (shouldHaveAnotherSegment || isOnCoverPage) {
      if (!isOnLockedChapter) {
        dispatch({
          type: READING_ACTION_TYPES.SET_READER_READY,
          payload: {
            ready: false,
          },
        });
      }

      if (currentChapter.isChapter && !isLastPageOfLockedChapter) {
        dispatch({
          type: ACTIVITY_ACTION_TYPES.SET_VISIBLE_ACTIVITIES,
          payload: {
            visibleActivities: [],
          },
        });
      }
    }
  }

  // if we're not at last page, go to next page...
  if (pages.canGoRight && !isLastPageOfLockedChapter) {
    return navigateNext(dispatch, getState);
  }

  const confirmationDialogShouldAppear =
      !displayConfirmation && (singleColumnLayout || isPractice) && !currentMode === READING_MODES.CHALLENGE;

  if (last(availableModes) === currentMode) {
    return dispatch(
      setConfirmationDialogVisibilty({
        isVisible: confirmationDialogShouldAppear,
        shouldProceed: true,
      }),
    );
  } if (currentMode === READING_MODES.CHALLENGE && postReading.questions.length > 0) {
    // only if there are remaining PR questions...
    if (postReading.remainingQuestions.length > 0) {
      return startPostReadingSeamlessly(dispatch, getState);
    }
    return finishPostReading();
  }
  return requestChangeMode(dispatch);
};

export function finishPostReading() {
  return dispatch => dispatch({
    type: READING_MODE_ACTION_TYPES.ALL_MODES_COMPLETED,
  });
}

export function goBackToActivityPage(targetPage) {
  return () => ReaderAPI.goToPageIndex(targetPage);
}

export const finishCompletionThreshold = (page) => (dispatch, getState) => {
  const eReaderState = getState().eReader;
  const unfinishedActivities = getUnfinishedActivities(eReaderState);
  const { quest, bookInfo: { attempts } } = eReaderState;
  const { lockedChapterDocument: chapterId } = quest;

  logRumAction('finishCompletionThreshold', { attempts, chapterId, unfinishedActivities });

  return ReaderAPI.goToPageIndex(page || 0).then(() => {
    dispatch(updateRemainingPostReadingQuestions());

    dispatch({
      type: READING_ACTION_TYPES.RETURN_TO_UNANSWERED_QUESTIONS,
      payload: {},
    });
  });
};

export const showUnansweredQuestions = () => (dispatch) => {
  dispatch({
    type: READING_ACTION_TYPES.SHOW_UNANSWERED_QUESTIONS,
  });

  dispatch(updateRemainingPostReadingQuestions());
};

export const tryAgain = (pageIndex = 0, isMastered = false) => (dispatch, getState) => {
  const { bookInfo: { attempts, bookId }, bookInfo, quest, accurateResponseData } = getState().eReader;
  const { isPractice, lockedChapterDocument: chapterId } = quest;

  const mode = READING_MODES.CHALLENGE;
  const bookAttempts = attempts + 1;

  logRumAction('tryAgain', { currentAttempts: attempts, chapterId });

  return ReaderAPI.goToPageIndex(pageIndex).then(() => {
    if (isMastered) {
      // isMastered should only ever be true for deep link
      // Need to differentiate when deep link chapter has been mastered (and submitted)
      // so as to not allow > 2 submissions but still access text
      dispatch({
        type: READING_ACTION_TYPES.SET_IS_DEEP_LINK_MASTERED,
        payload: true,
      });
    }

    dispatch({
      type: READING_MODE_ACTION_TYPES.MODE_CHANGED,
      payload: {
        mode,
      },
    });

    dispatch({
      type: READING_MODE_ACTION_TYPES.SET_SHOW_ALERTS,
      payload: false,
    });

    dispatch({
      type: READING_ACTION_TYPES.TRY_AGAIN,
      payload: {
        attempts: bookAttempts,
      },
    });

    setAccurateResponseData(bookInfo.bookId, {
      ...accurateResponseData,
      totalInstruction: 1,
    });

    setShowAlerts(bookId, false);
    setReadingMode(bookId, mode);
    setBookAttempts(bookId, { chapterId, isPractice }, bookAttempts);
    resetActivityAnswers(bookId, true);

    dispatch({
      type: READING_ACTION_TYPES.RETURN_TO_UNANSWERED_QUESTIONS,
      payload: {},
    });
  });
};

export function setStatus(bookId, status, debounceMs) {
  return (dispatch, getState) => {
    const state = getState();
    const questDescriptor = getQuestDescriptor(state);
    const gameId = `ereader.${bookId}`;

    return setBookStatus(bookId, questDescriptor, status, debounceMs).then(() => {
      const gameDetails = getLocalBookState(bookId);
      const bookInfo = {
        GameData: { gameDetails },
      };
      dispatch(updateEreaderStep({
        bookId: gameId,
        data: bookInfo,
      }));
      dispatch({
        type: READING_ACTION_TYPES.SET_STATUS,
        payload: {
          status,
        },
      });
    });
  };
}

export function startPostReadingSeamlessly(dispatch) {
  dispatch({
    type: READING_MODE_ACTION_TYPES.MODE_CHANGED,
    payload: { mode: READING_MODES.POST },
  });
  dispatch(updatePaginationStatus({
    canGoLeft: true,
    canGoRight: true,
  }));
}

export function returnToChallengeMode() {
  return (dispatch) => {
    dispatch({
      type: READING_MODE_ACTION_TYPES.RETURN_TO_CHALLENGE,
    });
    dispatch(updateRemainingPostReadingQuestions());
    dispatch(updatePaginationStatus({
      canGoLeft: true,
      canGoRight: false,
    }));
  };
}

export const setVoiceCaption = caption => ({
  type: CAPTIONS_ACTION_TYPES.SET_VOICE_CAPTIONS,
  payload: caption,
});

export const exitBook = () => (dispatch, getState) => {
  const state = getState();
  const isWorkshop = getIsSeason3(state);
  const {
    eReader: { bookInfo: { status } },
  } = state;

  dispatch(exitGame());
  dispatch({ type: READING_ACTION_TYPES.CLOSE_BOOK });
  if (isWorkshop) {
    dispatch(notifyEreaderStepProgress(status === BOOK_STATUS.COMPLETE));
  }
};
